import { useTranslation } from "react-i18next";
import type {
  BulkFormData,
  FormStructure,
  SoStepsOptions,
  StepDef,
} from "~/types/ui";
import {
  Stepper,
  StepperItem,
  StepperTrigger,
  StepperIndicator,
  StepperSeparator,
  StepperContent,
  StepperTitle,
  StepperDescription,
} from "~/components/ui/stepper";
import { useState } from "react";
import { Check } from "lucide-react";
import { SoForm } from "./so-form";

export function SteppedForm<T>({
  values,
  structure,
  onSubmit,
  stepsOptions,
  isBulkForm,
}: {
  values?: T | T[] | null;
  structure: FormStructure<T>;
  onSubmit: (data: T | BulkFormData<T>) => void;
  stepsOptions: SoStepsOptions<T>[];
  isBulkForm?: boolean;
}) {
  const [step, setStep] = useState(stepsOptions.at(0)?.step ?? 0);
  const [combinedData, setCombinedData] = useState(values);

  function handleOnSubmit(data: T | BulkFormData<T>) {
    setCombinedData(isBulkForm ? (data as BulkFormData<T>).items : (data as T));
    setStep(
      stepperSteps.at(stepperSteps.findIndex((x) => x.step == step) + 1)
        ?.step ?? step,
    );
  }

  const stepperSteps =
    stepsOptions.map((x) => {
      return {
        step: x.step,
        titleKey: x.titleKey,
        descriptionKey: x.descriptionKey,
        content: (
          <SoForm
            values={combinedData}
            structure={x.structure}
            onSubmit={handleOnSubmit}
            isBulkForm={isBulkForm}
            disableRowAlteration={x.disableRowAlteration}
          />
        ),
      } as StepDef;
    }) ?? [];

  return (
    <StepperComponent
      steps={stepperSteps}
      externalStep={step}
      onStepChange={(value) => setStep(value)}
    />
  );
}

export function StepperComponent({
  steps,
  externalStep,
  onStepChange,
}: {
  steps: StepDef[];
  externalStep?: number;
  onStepChange?: (step: number) => void;
}) {
  const { t } = useTranslation();
  const [step, setStep] = useState(steps.at(0)?.step ?? 0);

  return (
    <Stepper
      value={externalStep !== undefined ? externalStep : step}
      onValueChange={(value) =>
        externalStep !== undefined && onStepChange !== undefined
          ? onStepChange(value)
          : setStep(value)
      }
    >
      {steps.map((item, i) => (
        <StepperItem key={item.step} step={item.step}>
          {({ state }) => (
            <>
              <StepperTrigger
                step={item.step}
                className="flex flex-col items-center gap-2"
              >
                <StepperIndicator>
                  {state === "completed" ? <Check /> : item.step}
                </StepperIndicator>

                <div className="text-center">
                  <StepperTitle>{t(item.titleKey)}</StepperTitle>
                  {item.descriptionKey ? (
                    <StepperDescription>
                      {t(item.descriptionKey)}
                    </StepperDescription>
                  ) : (
                    <></>
                  )}
                </div>
              </StepperTrigger>
              {i < steps.length - 1 && <StepperSeparator />}
            </>
          )}
        </StepperItem>
      ))}
      {steps.map((item) => (
        <StepperContent
          key={item.step}
          step={item.step}
          className="flex-row w-full mt-2"
        >
          {item.content}
        </StepperContent>
      ))}
    </Stepper>
  );
}
